// src/pages/happinessreport/components/FeedbacksAccordion.tsx
/**
 * FeedbacksAccordion (corrigido)
 *
 * - Renderiza 2 accordions: Work Happiness & Further Feelings
 * - Usa os campos reais retornados pelo back-end: `answer` e `date_create`
 * - Usa CustomAccordion e CustomPagination do projeto para manter comportamento/estilo
 * - Ao mudar de página, reconstrói os filtros (sexes, ageRanges, groups) igual ao original
 *
 * Props esperadas (passar do controller/hook):
 *  - loadingWork: boolean
 *  - loadingFurther: boolean
 *  - workHappinessFeedbacksPageResults: FeedbacksPageResults
 *  - furtherFeelingsFeedbacksPageResults: FeedbacksPageResults
 *  - loadWorkHappinessFeedbacks: (page:number, year?:string, month?:string, filters?:any)=>Promise<void>
 *  - loadFurtherFeelingsFeedbacks: (page:number, year?:string, month?:string, filters?:any)=>Promise<void>
 *  - dateValue: string (YYYY-MM)
 *  - representationSelectedOptions: string[] (as labels)
 *  - selectedGroupOptions: string[] (IDs)
 *  - representationOptionsValues: mapping label -> numeric value (importado pelo page / hook)
 *  - renderDate: (dateStr?: string) => string
 */

import React, { useState } from 'react';
import CustomAccordion from '../../../components/accordion/accordion';
import CustomPagination from '../../../components/customPagination';
import { Card, Row, Col, Spinner } from 'react-bootstrap';
import { StyledTd, StyledTdQualitativeAnswer, StyledTh, StyledTr } from '../../settings/styles';
import { StyledDivOverflow, StyledRowOverflow } from '../../../components/dashboard/attentionPoints/styles';
import { useTranslation } from 'react-i18next';

type FeedbacksPageResults = {
  total_items: number;
  total_pages: number;
  current_page: number;
  data: any[];
};

type Props = {
  loadingWork: boolean;
  loadingFurther: boolean;
  workHappinessFeedbacksPageResults: FeedbacksPageResults;
  furtherFeelingsFeedbacksPageResults: FeedbacksPageResults;
  loadWorkHappinessFeedbacks: (page: number, year?: string, month?: string, filters?: any) => Promise<void>;
  loadFurtherFeelingsFeedbacks: (page: number, year?: string, month?: string, filters?: any) => Promise<void>;
  dateValue: string;
  representationSelectedOptions: string[]; // labels
  selectedGroupOptions: string[]; // values/ids
  representationOptionsValues: any; // objeto de mapping label -> value
  renderDate: (date?: string) => string;
  isGroupResponsible?: boolean;
};

const FeedbacksAccordion: React.FC<Props> = ({
  loadingWork,
  loadingFurther,
  workHappinessFeedbacksPageResults,
  furtherFeelingsFeedbacksPageResults,
  loadWorkHappinessFeedbacks,
  loadFurtherFeelingsFeedbacks,
  dateValue,
  representationSelectedOptions,
  selectedGroupOptions,
  representationOptionsValues,
  renderDate,
  isGroupResponsible = false
}) => {
  const { t } = useTranslation('reports');

  const perPage = 10; // o original assume páginas de 10 itens nas mensagens de contagem

  // estados de abertura dos accordions (se quiser controlar a abertura/fechamento)
  const [workHappinessFeedbacksAccordionIsOpen, setWorkHappinessFeedbacksAccordionIsOpen] = React.useState<boolean>(true);
  const [furtherFeelingsFeedbacksAccordionIsOpen, setFurtherFeelingsFeedbacksAccordionIsOpen] = React.useState<boolean>(true);

  const buildFiltersFromRepresentation = () => {
    // selected representation labels -> backend values
    const selectedSexes = (representationSelectedOptions ?? [])
      .map((k: string) => representationOptionsValues[k])
      .filter((v: any) => typeof v === 'number');

    const selectedAgeRanges = (representationSelectedOptions ?? [])
      .map((k: string) => representationOptionsValues[k])
      .filter((v: any) => typeof v === 'object');

    const filters = {
      sexes: selectedSexes ?? [],
      ageRanges: selectedAgeRanges ?? [],
      groups: selectedGroupOptions ?? []
    };

    return filters;
  };

  const handleWorkPageChange = async (page: number) => {
    const [year, month] = (dateValue || '').split('-');
    const filters = buildFiltersFromRepresentation();
    await loadWorkHappinessFeedbacks(page, year, month, filters);
  };

  const handleFurtherPageChange = async (page: number) => {
    const [year, month] = (dateValue || '').split('-');
    const filters = buildFiltersFromRepresentation();
    await loadFurtherFeelingsFeedbacks(page, year, month, filters);
  };

  const renderCountText = (pageResults: FeedbacksPageResults) => {
    const { total_items, total_pages, current_page } = pageResults;
    if (!total_items) return t('noAnswers');

    const start = current_page === total_pages ? (((current_page - 1) * perPage) + 1) : ((current_page * perPage) - (perPage - 1));
    const end = current_page === total_pages ? total_items : (current_page * perPage);
    return t('showingAnswers', { start, end, total: total_items });
  };

  return (
    <>
      <div style={{ marginBottom: '2rem' }}>
        <CustomAccordion
          key={'work-happiness-feedbacks'}
          feedbackAccordion={true}
          isOpen={workHappinessFeedbacksAccordionIsOpen}
          // a página controla estado de abertura se preferir; aqui mantive aberto por padrão
          setIsOpen={() => setWorkHappinessFeedbacksAccordionIsOpen(!workHappinessFeedbacksAccordionIsOpen)}
          title={
            <>
              {t('feedbackQuestion')}
              {isGroupResponsible && (
                <div style={{ fontSize: '0.7em', color: '#6b6b6b', marginTop: '0.2em' }}>
                  {t('groupResponsible')}
                </div>
              )}
            </>
          }
          accordionKey='work-happiness'

        >
          {loadingWork ? (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
              <Spinner style={{ color: '#c9c9c9' }} />
            </div>
          ) : (
            <>
              {workHappinessFeedbacksPageResults.data && workHappinessFeedbacksPageResults.data.length > 0 ? (
                <StyledDivOverflow style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                  {/* <div style={{ marginBottom: '.75rem', color: '#3C4043' }}></div> */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#FFF', minWidth: '800px' }}>
                    <thead>
                      <tr>
                        <th style={{ color: '#3C4043', padding: '1.25em', borderBottom: "3px solid #F1F1F1", margin: "0 1em" }}>{renderCountText(workHappinessFeedbacksPageResults)}</th>
                        <StyledTh style={{ color: '#3C4043', padding: '1.25em', borderBottom: "3px solid #F1F1F1", margin: "0 1em" }}>{t('date')}</StyledTh>
                      </tr>
                    </thead>
                    <tbody>
                      {workHappinessFeedbacksPageResults.data.map((item: any, idx: number) => (
                        <StyledTr key={idx} style={{ borderBottom: '3px solid #F5F7F8' }}>
                          <StyledTdQualitativeAnswer style={{ padding: '1.25em', color: '#878B8E' }}>{item?.answer ?? '-'}</StyledTdQualitativeAnswer>
                          <StyledTd style={{ padding: '1.25em', color: '#878B8E' }}>{renderDate(item?.date_create)}</StyledTd>
                        </StyledTr>
                      ))}
                    </tbody>
                  </table>

                  {/* Paginação - delega ao handler */}
                  <div style={{ margin: '1.2em 0' }}>
                    <CustomPagination
                      data={workHappinessFeedbacksPageResults}
                      handlePageChange={(p: number) => handleWorkPageChange(p)}
                    />
                  </div>
                </StyledDivOverflow>
              ) : (
                <div style={{ padding: '1.25rem', color: '#6b6b6b' }}>
                  {t('noRegistered')}
                </div>
              )}
            </>
          )}
        </CustomAccordion>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <CustomAccordion
          key={'further-feelings-feedbacks'}
          feedbackAccordion={true}
          isOpen={furtherFeelingsFeedbacksAccordionIsOpen}
          setIsOpen={() => setFurtherFeelingsFeedbacksAccordionIsOpen(!furtherFeelingsFeedbacksAccordionIsOpen)}
          title={
            <>
              {t('furtherQuestion')}
              {isGroupResponsible && (
                <div style={{ fontSize: '0.7em', color: '#6b6b6b', marginTop: '0.2em' }}>
                  {t('groupResponsible')}
                </div>
              )}
            </>
          }
          accordionKey='further-feelings'
        >
          {loadingFurther ? (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
              <Spinner style={{ color: '#c9c9c9' }} />
            </div>
          ) : (
            <>
              {furtherFeelingsFeedbacksPageResults.data && furtherFeelingsFeedbacksPageResults.data.length > 0 ? (
                <StyledDivOverflow style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#FFF', minWidth: '900px' }}>
                    <thead>
                      <tr>
                        <th style={{ color: '#3C4043', padding: '1.25em', borderBottom: "3px solid #F1F1F1", margin: "0 1em" }}>{renderCountText(furtherFeelingsFeedbacksPageResults)}</th>
                        <StyledTh style={{ color: '#3C4043', padding: '1em', borderBottom: "3px solid #F1F1F1", margin: "0 1em" }}>{t('date')}</StyledTh>
                      </tr>
                    </thead>

                    <tbody>
                      {furtherFeelingsFeedbacksPageResults.data.map((item: any, idx: number) => (
                        <StyledTr key={idx} style={{ borderBottom: '3px solid #F5F7F8' }}>
                          <StyledTdQualitativeAnswer style={{ padding: '1.25em', color: '#878B8E' }}>{item?.answer ?? '-'}</StyledTdQualitativeAnswer>
                          <StyledTd style={{ padding: '1.25em', color: '#878B8E' }}>{renderDate(item?.date_create)}</StyledTd>
                        </StyledTr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ margin: '1.2em 0' }}>
                    <CustomPagination
                      data={furtherFeelingsFeedbacksPageResults}
                      handlePageChange={(p: number) => handleFurtherPageChange(p)}
                    />
                  </div>
                </StyledDivOverflow>
              ) : (
                <div style={{ padding: '1.25rem', color: '#6b6b6b' }}>
                  {t('noRegistered')}
                </div>
              )}
            </>
          )}
        </CustomAccordion>
      </div>
    </>
  );
};

export default FeedbacksAccordion;
